/**
 * stackFillPlan.js — кому какая серия достанется в многооконной раскладке.
 *
 * Правило вынесено из uiManager без единого импорта, потому что проверять его
 * иначе пришлось бы поднятием всего приложения, а ошибка здесь выглядит как
 * чёрный прямоугольник — то есть неотличимо от «серия не загрузилась».
 *
 * Что было. Раскладки 1×2 и 2×2 заполняли ровно одно окно: loadStack звался
 * для активного вьюпорта, остальные оставались чёрными навсегда. Положить в
 * них что-то можно было только вручную — нажать на пустое окно и выбрать
 * серию в списке, — но об этом нигде не сказано, и человек видел «четыре
 * окна» с одним изображением и тремя дырами.
 *
 * Что теперь. Активное окно получает выбранную серию, остальные — другие
 * серии исследования, самые полные вперёд. Если серий меньше, чем окон, хвост
 * получает ту же самую: одинаковый снимок в двух окнах осмыслен (разные окна
 * плотности, разные срезы), чёрный прямоугольник — нет.
 */

/**
 * @param {object} p
 * @param {string[]} p.vpIds все окна раскладки, в порядке сетки
 * @param {string} [p.activeId] окно, на которое нажали
 * @param {string} p.seriesUID выбранная серия
 * @param {Array<{seriesUID: string, count?: number}>} [p.others] прочие серии
 * @param {string[]} [p.occupied] окна, куда серию клали руками — не трогаем
 * @returns {Array<{vpId: string, seriesUID: string, active: boolean}>}
 */
export function planStackFill({ vpIds, activeId, seriesUID, others = [], occupied = [] }) {
  const ids = Array.isArray(vpIds) ? vpIds.filter(Boolean) : [];
  if (!ids.length || !seriesUID) return [];

  const active = ids.includes(activeId) ? activeId : ids[0];
  const busy = new Set((occupied || []).filter(id => id !== active));

  // Самые полные серии вперёд: локалайзер из трёх снимков в соседнем окне
  // пользы не приносит, а место занимает.
  const pool = (others || [])
    .filter(s => s && s.seriesUID && s.seriesUID !== seriesUID)
    .slice()
    .sort((a, b) => (b.count || 0) - (a.count || 0));

  const plan = [{ vpId: active, seriesUID, active: true }];
  let next = 0;
  for (const vpId of ids) {
    if (vpId === active || busy.has(vpId)) continue;
    const pick = next < pool.length ? pool[next++].seriesUID : seriesUID;
    plan.push({ vpId, seriesUID: pick, active: false });
  }
  return plan;
}

export default { planStackFill };
